"use client"

import React from "react"
import { motion } from "framer-motion"
import { useForm, ValidationError } from "@formspree/react"
import "./App.css"

// Import images
import FounderImg from "./images/founderimg.png"
import TeamImg from "./images/team.png"
import backgroundimg from "./images/bgp12.png"
import serviceimg from "./images/serviceimg.png"
import moreservice from "./images/moreservice.png"
import promiseimg from "./images/promiseimg.png"
import eventimg from "./images/events.png"
import whyus from "./images/whyus.png"

// Fade-in Effect Wrapper
const FadeInSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
  >
    {children}
  </motion.div>
)

// Shared navigation function
const navigateToSection = (id) => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: "smooth" })
    // Update URL with hash instead of path
    window.history.pushState(null, null, `#${id}`)
  }
}

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  React.useEffect(() => {
    // Handle direct navigation to hash URLs
    const handleHashNavigation = () => {
      // Get the hash from the URL (remove the # symbol)
      const hash = window.location.hash.substring(1)

      if (hash) {
        const element = document.getElementById(hash)
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth" })
          }, 100)
        }
      }
    }

    // Run on initial load
    handleHashNavigation()

    // Also listen for hash changes
    window.addEventListener("hashchange", handleHashNavigation)

    // Make navbar always visible
    const navbar = document.querySelector(".navbar")
    navbar.style.transform = "translateY(0)"

    // Clean up event listener
    return () => {
      window.removeEventListener("hashchange", handleHashNavigation)
    }
  }, [])

  const scrollToSection = (id) => {
    navigateToSection(id)
    setMobileMenuOpen(false)
  }

  return (
    <nav className="navbar">
      <div className="container">
        <div className="logo">
          <a onClick={() => scrollToSection("hero")} className="hero-highlight">
            1
          </a>
          <a onClick={() => scrollToSection("hero")}>LearnTLS</a>
        </div>

        {/* Mobile menu toggle button */}
        <div className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <div className={`hamburger ${mobileMenuOpen ? "open" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <ul className={`nav-links ${mobileMenuOpen ? "mobile-active" : ""}`}>
          <li>
            <a onClick={() => scrollToSection("hero")}>Home</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("about")}>About</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("services")}>Services</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("why-choose-us")}>Why Choose Us</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("our-promise")}>Our Promise</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("founder")}>Founder</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("contact")}>Contact</a>
          </li>
          <li>
            <a onClick={() => scrollToSection("upcoming-events")}>Upcoming Events</a>
          </li>
        </ul>
      </div>
    </nav>
  )
}

// 1. Hero Component
const Hero = () => (
  <div
    className="section home-section"
    id="hero"
    data-bg-color="rgba(0,0,0,0.1)"
    style={{ marginBottom: 0, paddingBottom: 0 }}
  >
    <div
      className="hero-background"
      style={{
        backgroundImage: `url(${backgroundimg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "650px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingBottom: "30px",
      }}
    >
      <div className="hero-buttons">
        <a onClick={() => navigateToSection("services")} className="cta-button primary-button">
          Our Services
        </a>
        <a onClick={() => navigateToSection("contact")} className="cta-button secondary-button">
          Contact Us
        </a>
      </div>
    </div>
  </div>
)

// 2. About Component
const About = () => (
  <FadeInSection>
    <div
      id="about"
      className="section about-section"
      data-bg-color="var(--light-background)"
      style={{ marginTop: 0, paddingTop: 0, marginBottom: 0, paddingBottom: 0 }}
    >
      <div className="light-gradient-background">
        <div className="container">
          <h2 className="section-title">WHO WE ARE</h2>
          <div className="section-underline"></div>
          <div className="content-layout">
            <div className="text-column">
              <div className="about-card">
                <h3 className="about-card-title">PEDIGREE</h3>
                <p>
                  Founded by Indroneil, the creator of iProdigy– country's all-time most respected consulting
                  organization for Talent, Leadership & Organization transformation.
                </p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">PEOPLE</h3>
                <p>
                  A group of seasoned and accomplished coaches and consultants, with over 100 years of cumulative
                  experience across industries and expertise spanning Functional, Learning & Organizational Development
                  (L&OD) and Human Resources (HR) domains.
                </p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">PROMISE</h3>
                <p>Contribute to positively impacting your business bottom-line with our offerings.</p>
              </div>
            </div>
            <div className="image-column">
              <img src={TeamImg || "/placeholder.svg"} alt="Team" className="responsive-image" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeInSection>
)

// 3. Services Section
const Services = () => (
  <FadeInSection>
    <div id="services" className="section services-section">
      <div className="light-gradient-background">
        <div className="container">
          <h2 className="section-title" style={{ color: "var(--primary-color)" }}>
            Our Service Offerings
          </h2>
          <div className="section-underline"></div>
          <p className="section-description" style={{ color: "var(--text-color)" }}></p>

          <div className="content-layout">
            <div className="image-column">
              <img src={serviceimg || "/placeholder.svg"} alt="Our Services" className="responsive-image" />
            </div>
            <div className="text-column">
              <div className="about-card">
                <h3 className="about-card-title">Sourcing of pre-screened trainers and coaches</h3>
                <p>- Map track-record with requirements</p>
                <p>- Evaluation of subject-matter expertise and ability to enable learning</p>
                <p>- Participate in selection process</p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">Requirement gathering and analysis</h3>
                <p>- 3-dimensional 3-I discovery</p>
                <p>- Gap analysis and hypotheses</p>
                <p>- Learning and strategic recommendations</p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">Custom-curated content:</h3>
                <p>- Learner's Guide</p>
                <p>- Facilitator's Guide</p>
                <p>- PPT Deck</p>
              </div>
            </div>
          </div>

          <div className="content-layout" style={{ marginTop: "40px" }}>
            <div className="text-column">
              <div className="about-card">
                <h3 className="about-card-title">Program management for:</h3>
                <p>- Multi-batch projects</p>
                <p>- Multi-trainer engagements</p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">Upskilling managers as facilitators and coaches</h3>
                <p>- Enabling at personality, attitude and skill levels</p>
                <p>- Mock sessions </p>
                <p>- Observation and feedback of live sessions</p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">Impactful, immersive public workshops for:</h3>
                <p>- Individual contributors</p>
                <p>- Managers</p>
                <p>- Leaders</p>
              </div>
            </div>
            <div className="image-column">
              <img src={moreservice || "/placeholder.svg"} alt="More Services" className="responsive-image" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeInSection>
)

// 4. Why Choose Us Section
const WhyChooseUs = () => (
  <FadeInSection>
    <div
      id="why-choose-us"
      className="section why-choose-section"
      style={{ marginTop: 0, paddingTop: 0, marginBottom: 0, paddingBottom: 0 }}
    >
      <div className="gradient-background">
        <div className="container">
          <h2 className="section-title">Why Choose Us?</h2>
          <div className="section-underline"></div>
          <div className="content-layout">
            <div className="text-column">
              <div className="about-card">
                <div className="letter-container">
                  <span className="large-letter">E</span>
                  <h3 className="about-card-title">xtensive industry expertise</h3>
                </div>
              </div>

              <div className="about-card">
                <div className="letter-container">
                  <span className="large-letter">T</span>
                  <h3 className="about-card-title">ailored solutions designed to address your needs</h3>
                </div>
              </div>

              <div className="about-card">
                <div className="letter-container">
                  <span className="large-letter">P</span>
                  <h3 className="about-card-title">
                    roven track-record of contributing to measurable impact and results
                  </h3>
                </div>
              </div>
            </div>
            <div className="image-column">
              <div className="about-card">
                <h3 className="about-card-title">We are proven.</h3>
                <p>
                  We carry a legacy of having engaged with and enabled 60+ client organizations, across industries and
                  touched more than 20,000 lives across functions and responsibility bands.
                </p>
              </div>

              <div className="about-card" style={{ padding: 0, overflow: "hidden" }}>
                <img
                  src={whyus || "/placeholder.svg"}
                  alt="Team collaboration"
                  className="responsive-image"
                  style={{ borderRadius: "8px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeInSection>
)

// 5. Our Promise Section
const OurPromise = () => (
  <FadeInSection>
    <div
      id="our-promise"
      className="section our-promise-section"
      style={{ marginTop: 0, paddingTop: 0, marginBottom: 0, paddingBottom: 0 }}
    >
      <div className="light-gradient-background">
        <div className="container">
          <h2 className="section-title" style={{ color: "var(--primary-color)" }}>
            OUR PROMISE
          </h2>
          <div className="section-underline"></div>
          <p className="section-description" style={{ color: "var(--text-color)" }}></p>

          <div className="content-layout">
            <div className="image-column">
              <img src={promiseimg || "/placeholder.svg"} alt="Our Promise" className="responsive-image" />
            </div>
            <div className="text-column">
              <div className="about-card">
                <h3 className="about-card-title">Transformative shifts</h3>
                <p>
                  Enabling transformation is at the root of whatever we offer. Our services, therefore ensure
                  transformative shifts at individual, group and organizational levels.
                </p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">True learning that goes beyond information download</h3>
                <p>
                  Our trainers are well versed in leveraging The Adult Learning methodology, making sure that learners
                  actually learn. Several tools and methodologies are used to make the learning process immersive and
                  experiential.
                </p>
              </div>
              <div className="about-card">
                <h3 className="about-card-title">Application of learning in the workplace to drive business impact</h3>
                <p>
                  We use The 70-20-10 Model for learning. The model proposes that:
                  <br />
                  -70% of learning is OJT and happens hands-on
                  <br />
                  -20% of learning is social and happens through interactions with others
                  <br />
                  -10% from formal educational events
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeInSection>
)

// 6. Founder Component
const Founder = () => (
  <FadeInSection>
    <div
      id="founder"
      className="section founder-section"
      data-bg-color="var(--secondary-color)"
      style={{ marginTop: 0, paddingTop: 0, marginBottom: 0, paddingBottom: 0 }}
    >
      <div className="gradient-background">
        <div className="container">
          <h2 className="section-title">About The Founder</h2>
          <div className="section-underline"></div>
          <p className="section-description"></p>

          <div className="founder-content">
            <div className="founder-profile">
              <div className="founder-image-container">
                <img src={FounderImg || "/placeholder.svg"} alt="Founder" className="responsive-image founder-image" />
              </div>
              <div className="founder-details">
                <h3 className="founder-name">Indroneil Mukerjee</h3>
                <p className="founder-title">Founder & CEO</p>

                <div className="founder-bio">
                  <p>
                    Acclaimed as a Guru of Change by the Week magazine and a Top Ten Thinker by MTC Global, upheld by
                    his followers as a Human Alchemist and a global pioneer of Transformational Coaching.
                  </p>
                  <p>
                    He has had more than 60 business institutions as his clients, touching Lives of more than 20,000
                    individuals working with these organizations, across functions and responsibility bands.
                  </p>
                  <p>
                    Indroneil has developed several frameworks and approaches related to learning and leadership, which
                    today are proven and time-tested. Contrary to prevalent practices, his approach to learning is
                    inside-out – working with the deepest faculties of individuals to bring about radical shifts.
                  </p>
                  <p>Indroneil is also a published author and a hi-impact public speaker.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FadeInSection>
)

// 7. Contact Form Component
const Contact = () => {
  const [state, handleSubmit] = useForm("xeoalvqw")

  if (state.succeeded) {
    return (
      <FadeInSection>
        <div
          id="contact"
          className="section contact-section"
          data-bg-color="var(--background-color)"
          style={{ marginTop: 0, paddingTop: 0 }}
        >
          <div className="light-gradient-background">
            <div className="container">
              <h2 className="section-title" style={{ color: "var(--primary-color)" }}>
                Contact Us
              </h2>
              <div className="section-underline"></div>

              <div className="success-message-container">
                <div className="success-message">
                  <p>Thank you for your message! We'll be in touch soon.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="submit-button"
                    style={{ marginTop: "20px", maxWidth: "200px" }}
                  >
                    Send Another Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FadeInSection>
    )
  }

  return (
    <FadeInSection>
      <div
        id="contact"
        className="section contact-section"
        data-bg-color="var(--background-color)"
        style={{ marginTop: 0, paddingTop: 0 }}
      >
        <div className="light-gradient-background">
          <div className="container">
            <h2 className="section-title" style={{ color: "var(--primary-color)" }}>
              Contact Us
            </h2>
            <div className="section-underline"></div>

            <div className="contact-layout">
              <div className="contact-info">
                <h3 className="contact-heading" style={{ color: "var(--primary-color)" }}>
                  Get In Touch
                </h3>
                <p style={{ color: "var(--text-color)" }}>
                  Have questions or want to learn more? Fill out the form and we'll get back to you as soon as possible.
                </p>

                <div className="contact-details">
                  <div className="contact-item">
                    <div className="contact-icon-wrapper" style={{ backgroundColor: "rgba(30, 58, 138, 0.2)" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="contact-icon"
                        style={{ color: "var(--primary-color)" }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </div>
                    <div>
                      <h4 className="contact-label" style={{ color: "var(--primary-color)" }}>
                        Email
                      </h4>
                      <p className="contact-value" style={{ color: "var(--text-color)" }}>
                        reachme@indroneil.com
                      </p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper" style={{ backgroundColor: "rgba(30, 58, 138, 0.2)" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="contact-icon"
                        style={{ color: "var(--primary-color)" }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 className="contact-label" style={{ color: "var(--primary-color)" }}>
                        Phone
                      </h4>
                      <p className="contact-value" style={{ color: "var(--text-color)" }}>
                        +91 9611861132
                      </p>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper" style={{ backgroundColor: "rgba(30, 58, 138, 0.2)" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="contact-icon"
                        style={{ color: "var(--primary-color)" }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <div>
                      <h4 className="contact-label" style={{ color: "var(--primary-color)" }}>
                        Address
                      </h4>
                      <p className="contact-value" style={{ color: "var(--text-color)" }}>
                        Bangalore, India
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-form-container">
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input type="text" id="name" name="name" required />
                    <ValidationError prefix="Name" field="name" errors={state.errors} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" required />
                    <ValidationError prefix="Email" field="email" errors={state.errors} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" name="message" rows="5" required></textarea>
                    <ValidationError prefix="Message" field="message" errors={state.errors} />
                  </div>

                  <button type="submit" className="submit-button" disabled={state.submitting}>
                    {state.submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FadeInSection>
  )
}

// 7. Upcoming Events Component
// 7. Upcoming Events Component
// 7. Upcoming Events Component
const UpcomingEvents = () => {
  const handleViewPDF = () => {
    try {
      const pdfUrl = "/Festival_of_learning_3.pdf";
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error("PDF error:", error);
      alert("Error opening PDF. Please contact support.");
    }
  };

  const handleDownloadPDF = () => {
    try {
      const pdfUrl = "/Festival_of_learning_3.pdf";
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = "Festival_of_Learning_Calendar.pdf";
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error("Download error:", error);
      alert("Error downloading PDF. Please contact support.");
    }
  };

  return (
    <FadeInSection>
      <div
        id="upcoming-events"
        className="section upcoming-events-section"
        style={{ 
          marginTop: 0, 
          paddingTop: 0, 
          marginBottom: 0, 
          paddingBottom: 0,
          filter: 'none',
          WebkitFilter: 'none'
        }}
      >
        <div 
          className="light-gradient-background"
          style={{
            colorScheme: 'only light',
            forcedColorAdjust: 'none'
          }}
        >
          <div className="container">
            <h2 className="section-title" style={{ color: "var(--primary-color)" }}>
              Upcoming Events
            </h2>
            <div className="section-underline"></div>

            <div className="event-card">
              <div className="content-layout">
                <div className="image-column">
                  <img
                    src={eventimg || "/placeholder.svg"}
                    alt="Workshop Session"
                    className="responsive-image event-image"
                    style={{
                      colorProfile: 'sRGB',
                      renderingIntent: 'relative-colorimetric'
                    }}
                  />
                </div>
                <div className="text-column event-details">
                  <h3 className="event-title">Festival of Learning 2025</h3>
                  
                  <p className="event-description">
                    Join us for in-person 2-day (16-hour) workshops in Bangalore at{" "}
                    <strong>Royal Orchid Central</strong>, from July to December 2025.
                  </p>

                  <p className="event-highlight">
                    Build capabilities, enhance skills and drive business impact with our expert-led, immersive and
                    transformative sessions.
                  </p>

                  <ul className="event-features">
                    <li>
                      Unleash The Power Within to uncover unprecedented heights of excellence, as individuals and
                      leaders.
                    </li>
                    <li>
                      Develop innate capabilities and enhance skills in Managing Unconscious Bias, Enhancing Executive
                      Presence, Being Customer Centric, Human- centered Design Thinking, Enabling Discovery and
                      Development of Talents and Change Leadership.
                    </li>
                    <li>Drive positive business impact through immersive and transformative workshops.</li>
                    <li>Certificate of capability after successful completion of each workshop.</li>
                  </ul>

                  <div className="event-actions">
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      {/* View Button */}
                      <button 
                        className="cta-button primary-button" 
                        onClick={handleViewPDF}
                        style={{
                          padding: '12px 24px',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          minWidth: '150px'
                        }}
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View Calendar
                      </button>

                      {/* Download Button */}
                      <button 
                        className="cta-button secondary-button" 
                        onClick={handleDownloadPDF}
                        style={{
                          padding: '12px 24px',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flex: 1,
                          minWidth: '150px'
                        }}
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download PDF
                      </button>
                    </div>

                    <div className="event-info-box">
                      <p>Group discounts available for nominations of 5 or more individuals</p>
                      <p>
                        <a
                          href="https://chat.whatsapp.com/DxZVCWHekVD0kxrbq9bMRG"
                          className="register-link"
                        >
                          Register here for more information
                        </a>
                      </p>
                      <p>Write to reachme@indroneil.com for in-house workshops</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FadeInSection>
  )
}

// Footer Component
const Footer = () => {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-info">
            <div className="footer-logo">1LearnTLS</div>
            <p className="footer-description">Your partner in talent development and leadership transformation.</p>
            <div className="footer-social">
              <a href="https://www.linkedin.com/in/indroneilm/" className="social-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
              <a href="https://www.instagram.com/allthatbach_/" className="social-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a href="https://www.facebook.com/the.indroneil/" className="social-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-links-container">
            <div className="footer-links-column">
              <h3 className="footer-heading">Quick Links</h3>
              <ul className="footer-links">
                <li>
                  <a onClick={() => navigateToSection("hero")}>Home</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("about")}>About</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("services")}>Services</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("why-choose-us")}>Why Choose Us</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("our-promise")}>Our Promise</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("founder")}>Founder</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("upcoming-events")}>Upcoming Events</a>
                </li>
                <li>
                  <a onClick={() => navigateToSection("contact")}>Contact</a>
                </li>
              </ul>
            </div>

            <div className="footer-links-column">
              <h3 className="footer-heading">Contact</h3>
              <address className="footer-address">
                <p>Bangalore, India</p>
                <p>Email: reachme@indroneil.com</p>
                <p>Phone: +91 9611861132</p>
              </address>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} 1LearnTLS. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  )
}

// WhatsApp Floating Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/919611861132"
    className="whatsapp-float"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chat on WhatsApp"
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
)

// Main App Component
const App = () => {
  return (
    <>
      <Navigation />
      <main className="main-content">
        {/* All sections in the requested order */}
        <Hero />
        <About />
        <Services />
        <WhyChooseUs />
        <OurPromise />
        <Founder />
        <UpcomingEvents />
        <Contact />
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  )
}

export default App
